import { get, create, updatePatch, deleteResource } from "./apiService";
import {
  resolveBusinessRoute,
  withBusinessContext,
} from "../utils/businessRoute";

export interface UserAddress {
  street?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
}

export interface UserProfile {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  countryCode?: string;
  country?: string;
  address?: UserAddress;
  idNumber?: string;
  title?: string;
  marketingConsent?: boolean;
  kycStatus?: string;
  twoFactorAuth?: boolean;
  preferences?: UserPreferences;
  notificationPreferences?: NotificationPreferences;
  pendingCreditsReceived?: PendingCreditReceived[];
}

export interface PendingCreditReceived {
  paymentId: string;
  transactionId: string;
  amount: string;
  currency: string;
  fromSender: string;
  receivedAt: string;
}

export interface UserPreferences {
  localCurrency?: string;
  performancePeriod?: PerformancePeriod;
  language?: string;
  theme?: "light" | "dark";
  showBalances?: boolean;
  hideZeroBalances?: boolean;
  postTradeSettlements?: boolean;
}

export type PerformancePeriod = "daily" | "weekly" | "monthly";

const PERFORMANCE_PERIODS: readonly PerformancePeriod[] = [
  "daily",
  "weekly",
  "monthly",
];

function isPerformancePeriod(value: string): value is PerformancePeriod {
  return PERFORMANCE_PERIODS.includes(value as PerformancePeriod);
}

export function normalizePerformancePeriod(value: string): PerformancePeriod {
  const normalized = value.trim().toLowerCase();

  if (!isPerformancePeriod(normalized)) {
    throw new Error("Unsupported performance timeframe");
  }

  return normalized;
}

export interface SupportedCurrencyAsset {
  _id?: string;
  symbol?: string;
  code?: string;
  name?: string;
  image?: string;
  imageUrl?: string;
  type?: string;
}

export interface NotificationPreferences {
  allowEmailNotification?: boolean;
  allowSmsNotification?: boolean;
  allowPushNotification?: boolean;
  accountActivity?: boolean;
  priceAlerts?: boolean;
  productAnnouncements?: boolean;
}

export interface CompleteProfilePayload {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  termsAndConditions: boolean;
  dateOfBirth?: string;
  countryCode?: string;
  country?: string;
  address?: UserAddress;
  idNumber?: string;
  title?: string;
  marketingConsent?: boolean;
  employmentStatus?: string;
  sourceOfFunds?: string;
  monthlySalaryRange?: string;
}

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  countryCode?: string;
  country?: string;
  address?: UserAddress;
  idNumber?: string;
  title?: string;
  marketingConsent?: boolean;
  employmentStatus?: string;
  sourceOfFunds?: string;
  monthlySalaryRange?: string;
}

export interface ChangePasswordPayload {
  oldPassword: string;
  newPassword: string;
}

export interface PinPayload {
  pin: string;
}

export type PinResetRequestStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "expired";

export type PinResetRequestApprovalRoute = "business_owner" | "platform_admin";

export interface BusinessSuitePinResetRequest {
  _id: string;
  requesterId: string;
  businessId: string;
  context: "business_suite";
  approvalRoute: PinResetRequestApprovalRoute;
  status: PinResetRequestStatus;
  requester: {
    name: string;
    email: string;
  };
  business: {
    businessName: string;
  };
  role: {
    key: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  decidedAt?: string;
  decisionReason?: string;
  alreadyPending?: boolean;
}

export interface BusinessSuitePinResetRequestPayload {
  businessId: string;
  context?: "business_suite";
}

export interface PinResetDecisionPayload {
  reason?: string;
}

export interface KycStatus {
  status: string;
  [key: string]: unknown;
}

export interface ProfileStatus {
  completed: boolean;
  [key: string]: unknown;
}

export interface UserFeatures {
  platform: unknown[];
  businesses: unknown[];
}

export const getUser = async (
  businessId?: string | null
): Promise<UserProfile> => {
  if (businessId && businessId.trim()) {
    return get<UserProfile>(
      resolveBusinessRoute(businessId, "/api/v2/me"),
      withBusinessContext(businessId)
    );
  }
  return get<UserProfile>("/api/v2/user/me");
};

export const getProfileStatus = async (): Promise<ProfileStatus> => {
  return get<ProfileStatus>("/api/v2/user/profile-status");
};

export const completeProfile = async (
  data: CompleteProfilePayload
): Promise<unknown> => {
  return create("/api/v2/user/complete-profile", data);
};

export const updateProfile = async (
  data: UpdateProfilePayload
): Promise<unknown> => {
  return updatePatch("/api/v2/user/update-profile", {}, data);
};

export const markPendingCreditsSeen = async (
  paymentIds: string[]
): Promise<{ updated: number }> => {
  return create<{ updated: number }, { paymentIds: string[] }>(
    "/api/v2/user/pending-credits/seen",
    { paymentIds }
  );
};

export const deleteAccount = async (pin: string): Promise<unknown> => {
  return deleteResource("/api/v2/user/delete-account", {}, { pin });
};

export const updatePreferences = async (
  preferences: UserPreferences
): Promise<UserPreferences> => {
  return updatePatch<UserPreferences, UserPreferences>(
    "/api/v2/user/preferences",
    {},
    preferences
  );
};

export const setPrefCurrency = async (
  localCurrency: string
): Promise<UserPreferences> => {
  return updatePreferences({ localCurrency });
};

export const setTimeframe = async (
  performancePeriod: string
): Promise<UserPreferences> => {
  return updatePreferences({
    performancePeriod: normalizePerformancePeriod(performancePeriod),
  });
};

export const setShowBalances = async (
  showBalances: boolean
): Promise<UserPreferences> => {
  return updatePreferences({ showBalances });
};

export const setHideZeroBalances = async (
  hideZeroBalances: boolean
): Promise<UserPreferences> => {
  return updatePreferences({ hideZeroBalances });
};

export const setTheme = async (
  theme: "light" | "dark"
): Promise<UserPreferences> => {
  return updatePreferences({ theme });
};

export const setLanguage = async (
  language: string
): Promise<UserPreferences> => {
  return updatePreferences({ language });
};

export const updateNotificationPreferences = async (
  prefs: NotificationPreferences
): Promise<unknown> => {
  return updatePatch("/api/v2/user/notifications", {}, prefs);
};

export const setNotifChange = async (data: {
  type: string;
  state: boolean;
}): Promise<unknown> => {
  return updateNotificationPreferences({
    [data.type]: data.state,
  } as NotificationPreferences);
};

export const unsubscribeEmail = async (): Promise<unknown> => {
  return updateNotificationPreferences({ allowEmailNotification: false });
};

export const changePassword = async (
  data: ChangePasswordPayload
): Promise<unknown> => {
  return create("/api/v2/user/change-password", data);
};

export const createPin = async (pin: string): Promise<unknown> => {
  return create("/api/v2/user/create-pin", { pin });
};

export const verifyPin = async (pin: string): Promise<unknown> => {
  return create("/api/v2/user/verify-pin", { pin });
};

export const createBusinessSuitePinResetRequest = async (
  data: BusinessSuitePinResetRequestPayload
): Promise<BusinessSuitePinResetRequest> => {
  return create<
    BusinessSuitePinResetRequest,
    BusinessSuitePinResetRequestPayload
  >("/api/v2/pin/reset-requests", {
    ...data,
    context: data.context ?? "business_suite",
  });
};

const inFlightPinResetStatus = new Map<
  string,
  Promise<BusinessSuitePinResetRequest | null>
>();

export const getBusinessSuitePinResetRequestStatus = async (
  businessId: string
): Promise<BusinessSuitePinResetRequest | null> => {
  const existing = inFlightPinResetStatus.get(businessId);
  if (existing) {
    return existing;
  }

  const pending = get<BusinessSuitePinResetRequest | null>(
    "/api/v2/pin/reset-requests/status",
    {
      businessId,
      context: "business_suite",
    }
  ).finally(() => {
    if (inFlightPinResetStatus.get(businessId) === pending) {
      inFlightPinResetStatus.delete(businessId);
    }
  });

  inFlightPinResetStatus.set(businessId, pending);
  return pending;
};

export const approveBusinessSuitePinResetRequest = async (
  requestId: string,
  data: PinResetDecisionPayload = {}
): Promise<BusinessSuitePinResetRequest> => {
  return create<BusinessSuitePinResetRequest, PinResetDecisionPayload>(
    `/api/v2/pin/reset-requests/${requestId}/approve`,
    data
  );
};

export const rejectBusinessSuitePinResetRequest = async (
  requestId: string,
  data: PinResetDecisionPayload = {}
): Promise<BusinessSuitePinResetRequest> => {
  return create<BusinessSuitePinResetRequest, PinResetDecisionPayload>(
    `/api/v2/pin/reset-requests/${requestId}/reject`,
    data
  );
};

export const getKycStatus = async (): Promise<KycStatus> => {
  return get<KycStatus>("/api/v2/user/kyc-status");
};

export const getUserFeatures = async (
  platform?: "web" | "mobile"
): Promise<UserFeatures> => {
  const params: Record<string, unknown> = {};
  if (platform) params.platform = platform;
  return get<UserFeatures>("/api/v2/user/me/features", params);
};

export const setTimeframeCall = async (timeframe: string): Promise<unknown> => {
  return setTimeframe(timeframe);
};

export const setToggelZero = async (): Promise<unknown> => {
  return setHideZeroBalances(true);
};

export const setZeroBalances = async (): Promise<unknown> => {
  return setHideZeroBalances(true);
};

export const setWalletCurrency = async (
  localCurrency: string
): Promise<unknown> => {
  return setPrefCurrency(localCurrency);
};

export const getUserLegacy = getUser;

export const getResendEmail = async () =>
  get("/api/v2/user/resend-verification");

export const getSupportedCurrencies = async (): Promise<
  SupportedCurrencyAsset[]
> =>
  get<SupportedCurrencyAsset[]>("/api/v2/assets", {
    supportedLocalCurrencies: "true",
  });

export const uploadUserDocuments = async (
  formData: FormData,
  headers?: Record<string, string>
): Promise<unknown> => {
  const { createWithFile } = await import("./apiService");
  return createWithFile("/api/v2/user/documents", formData);
};
